import { enqueueSnackbar } from 'notistack';
import { useState, Activity, useEffect } from 'react';

import { IconPlus, IconSearch } from '@tabler/icons-react';

import Button from '@/components/Button/Button';
import ConfirmationModal from '@/components/ConfirmationModal';
import PageEmptyState from '@/components/EmptyState/PageEmptyState';
import Input from '@/components/Input/Input';
import Pagination from '@/components/Pagination';
import SkeletonLoader from '@/components/SkeletonLoader';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import { deleteFAQ, fetchFAQs } from '@/service/editorialService';
import { IFaqData } from '@/types';
import { IDeleteFaqPayload } from '@/types/payload';

import FAQItem from './componenets/FAQItem';
import AddEditFaqModal from './modals/AddEditFaqModal';

const PAGE_SIZE = 6;

const FAQManagement = () => {
  const { formatText } = useFormatText();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search?.trim(), 500);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFaq, setSelectedFaq] = useState<IFaqData | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [confirmationLoading, setConfirmationLoading] = useState(false);

  const [faqs, setFaqs] = useState<IFaqData[]>([]);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  const fetchFaqsData = async () => {
    setLoading(true);

    try {
      const queryParams = {
        search: debouncedSearch || undefined,
        limit: PAGE_SIZE,
        offset: (currentPage - 1) * PAGE_SIZE,
      };

      const response = await fetchFAQs(queryParams);

      if (response?.status) {
        setFaqs(response?.data || []);
        setTotal(response?.total || 0);
      } else {
        console.error(response?.message || 'Failed to fetch FAQs');
      }
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setLoading(false);
    }
  };
  const handleEdit = (faq: IFaqData) => {
    setSelectedFaq(faq);
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setSelectedFaq(null);
    setIsModalOpen(true);
  };

  const handleDelete = (faq: IFaqData) => {
    setSelectedFaq(faq);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedFaq) return;

    setConfirmationLoading(true);

    try {
      const payload: IDeleteFaqPayload = {
        faq_id: selectedFaq.id,
      };

      const response = await deleteFAQ(payload);

      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });

        // If we're on a page other than the first and deleting the last item
        if (currentPage > 1 && faqs.length === 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchFaqsData();
        }
      } else {
        enqueueSnackbar(response?.message, { variant: 'error' });
      }
    } catch (error) {
      console.error('Error deleting FAQ:', error);
    } finally {
      setConfirmationLoading(false);
      setIsDeleteModalOpen(false);
      setSelectedFaq(null);
    }
  };

  useEffect(() => {
    fetchFaqsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, currentPage]);

  const isEmpty = !loading && total === 0 && debouncedSearch.length === 0;
  const isEmptySearch =
    !loading && debouncedSearch.length > 0 && faqs.length === 0;

  return (
    <>
      {isEmpty ? (
        <PageEmptyState
          image="/assets/svg/empty-editorial.svg"
          title={formatText('editorial.faq.empty-title')}
          description={formatText('editorial.faq.empty-desc')}
          buttonLabel={formatText('editorial.faq.actions.add-btn')}
          buttonIcon={<IconPlus size={16} />}
          onButtonClick={handleAdd}
        />
      ) : (
        <>
          {/* Header */}
          <div className="mb-6 flex flex-wrap justify-between items-center gap-2.5 ps-1">
            <Input
              name="search"
              placeholder={formatText('common.actions.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={{
                icon: <IconSearch size={20} className="text-neutral-500" />,
              }}
              className="max-w-77 md:min-w-77 w-full"
              inputClassName="py-[13px]"
            />
            <Button
              icon={<IconPlus size={20} />}
              label={formatText('editorial.faq.actions.add-btn')}
              className="h-12 px-4"
              variant="primary"
              onClick={handleAdd}
            />
          </div>

          {loading ? (
            <div className="grid grid-cols-1 gap-6">
              {[1, 2, 3].map((item) => (
                <SkeletonLoader
                  key={item}
                  className="w-full h-30 rounded-xl!"
                  variant="rect"
                />
              ))}
            </div>
          ) : isEmptySearch ? (
            <PageEmptyState
              image="/assets/svg/not-found.svg"
              title={formatText('common.table.no-search-title')}
              description={formatText('common.table.no-search-subtitle')}
              containerClassName="h-[65vh]!"
            />
          ) : (
            <>
              <div className="grid grid-cols-1 gap-6">
                {faqs?.map((faq) => (
                  <FAQItem
                    key={faq?.id}
                    question={faq?.question}
                    answer={faq?.answer}
                    onEdit={() => handleEdit(faq)}
                    onDelete={() => handleDelete(faq)}
                  />
                ))}
              </div>
              <Pagination
                total={total}
                currentPage={currentPage}
                pageSize={PAGE_SIZE}
                setCurrentPage={setCurrentPage}
                isLoading={loading}
                className="border-none! mt-6 px-2!"
              />
            </>
          )}
        </>
      )}

      <Activity mode={isModalOpen ? 'visible' : 'hidden'}>
        <AddEditFaqModal
          setIsOpen={setIsModalOpen}
          refetch={fetchFaqsData}
          selectedFaqId={selectedFaq?.id ?? null}
        />
      </Activity>

      <Activity mode={isDeleteModalOpen ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={isDeleteModalOpen}
          setIsOpen={setIsDeleteModalOpen}
          title="editorial.faq.actions.delete-faq-title"
          descriptionPrefix="editorial.faq.actions.delete-faq-prefix"
          descriptionSuffix="editorial.faq.actions.delete-faq-suffix"
          confirmLabel={formatText('editorial.faq.actions.delete-faq-button')}
          onConfirm={handleConfirmDelete}
          isLoading={confirmationLoading}
          isDestructive
        />
      </Activity>
    </>
  );
};

export default FAQManagement;
